import { useCallback, useEffect, useMemo, useState } from "react";
import { MessageSquare, Search, Download, Phone, Mail, MessageCircle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { cn, initialsOf } from "@/lib/utils";
import { useLang, translateFr as tfr } from "@/i18n";
import { fmtDate } from "@/pages/Reperes";
import OutreachDetail from "@/components/outreach/OutreachDetail";
import StatusTabs from "@/components/prospecting/StatusTabs";
import WorkspaceBreadcrumb from "@/components/prospecting/WorkspaceBreadcrumb";

export const STATES = ["EN_ATTENTE", "A_RELANCER", "REPONSE_RECUE", "SANS_REPONSE", "REFUS"];
export const STATE_FR = {
  EN_ATTENTE: tfr("En attente"), A_RELANCER: tfr("À relancer"), REPONSE_RECUE: tfr("Réponse reçue"),
  SANS_REPONSE: tfr("Sans réponse"), REFUS: tfr("Refus"),
};
export const CHANNEL_ICON = { whatsapp: MessageCircle, email: Mail, phone: Phone };
export const CHANNEL_FR = { whatsapp: "WhatsApp", email: "Email", phone: tfr("Téléphone") };

export const followUpLabel = (days, tf) => {
  if (days === null || days === undefined) return null;
  if (days < 0) return { text: `${tf("en retard de")} ${-days} j`, tone: "overdue" };
  if (days === 0) return { text: tf("aujourd'hui"), tone: "today" };
  return { text: `${tf("dans")} ${days} j`, tone: "ok" };
};

export default function PrisesDeContact() {
  const { tf } = useLang();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("EN_ATTENTE");
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");
  const [fChannel, setFChannel] = useState("all");
  const [fSport, setFSport] = useState("all");
  const [mobileOpen, setMobileOpen] = useState(false);

  const load = useCallback(async () => {
    try { setData((await api.get("/prises-de-contact")).data); }
    catch { toast.error(tf("Erreur de chargement")); }
  }, [tf]);
  useEffect(() => { load(); }, [load]);

  const athletes = useMemo(() => data?.athletes || [], [data]);
  const counts = data?.counts || {};
  const sports = useMemo(() => [...new Set(athletes.map((a) => a.sport))], [athletes]);

  const list = useMemo(() => {
    let arr = athletes.filter((a) => a.contact_state === tab);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      arr = arr.filter((a) => `${a.name} ${a.club} ${a.position}`.toLowerCase().includes(s));
    }
    if (fChannel !== "all") arr = arr.filter((a) => a.first_contact_channel === fChannel);
    if (fSport !== "all") arr = arr.filter((a) => a.sport === fSport);
    const nf = (a) => a.next_follow_up_days ?? 9999;
    if (tab === "EN_ATTENTE" || tab === "A_RELANCER") arr.sort((a, b) => nf(a) - nf(b));
    else if (tab === "REPONSE_RECUE") arr.sort((a, b) => (a.last_response_days_ago ?? 999) - (b.last_response_days_ago ?? 999));
    else arr.sort((a, b) => (b.last_response_at || b.first_contact_at || "").localeCompare(a.last_response_at || a.first_contact_at || ""));
    return arr;
  }, [athletes, tab, q, fChannel, fSport]);

  const current = useMemo(
    () => athletes.find((a) => a.id === selected && a.contact_state === tab) || list[0] || null,
    [athletes, selected, list, tab]
  );

  const exportCsv = () => {
    const head = ["Athlète", "Sport", "Poste", "Club", "Niveau", "1er contact", "Canal", "Relances", "Prochaine relance", "État"].map((s) => tfr(s));
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [head, ...list.map((a) => [
      a.name, a.sport, a.position || "", a.club || "Sans club", a.level || "",
      (a.first_contact_at || "").slice(0, 10), CHANNEL_FR[a.first_contact_channel] || "",
      `${a.contact_attempts}/${a.max_attempts}`, a.next_follow_up_date || "", STATE_FR[a.contact_state],
    ])].map((r) => r.map(esc).join(";"));
    const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const el = document.createElement("a");
    el.href = URL.createObjectURL(blob);
    el.download = "athlynx_prises_de_contact.csv";
    el.click();
    URL.revokeObjectURL(el.href);
  };

  return (
    <div className="animate-fade-in p-4 md:p-6 space-y-4 md:space-y-5 max-w-[1600px] mx-auto min-w-0" data-testid="outreach-page">
      {/* Breadcrumb + header */}
      <div>
        <WorkspaceBreadcrumb currentLabel="Prises de contact" testid="outreach-breadcrumb-dashboard" />
        <div className="flex flex-wrap items-start justify-between gap-4 mt-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="h-11 w-11 rounded-xl bg-athlynx/10 border border-athlynx/25 flex items-center justify-center shrink-0">
            <MessageSquare className="h-5 w-5 text-athlynx" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight">{tf("Prises de contact")}</h1>
            <p className="text-sm font-medium text-foreground/80">{tf("Qui m'a répondu, et qui dois-je relancer ?")}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{tf("Suivez vos prises de contact et relancez au bon moment.")}</p>
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-card px-4 py-3 flex items-center gap-4" data-testid="outreach-summary">
          <div>
            <div className="text-3xl font-bold text-foreground tabular-nums leading-none" data-testid="outreach-stock">{counts.total ?? "—"}</div>
            <div className="text-[11px] text-muted-foreground mt-1">{tf("athlètes en prise de contact")}</div>
          </div>
          <div className="text-[11px] text-muted-foreground border-l border-border pl-4">
            +{data?.flow_7d?.entries ?? 0} {tf((data?.flow_7d?.entries ?? 0) > 1 ? "entrées" : "entrée")} · {data?.flow_7d?.exits ?? 0} {tf((data?.flow_7d?.exits ?? 0) > 1 ? "sorties" : "sortie")} {tf("ces 7 jours")}
          </div>
        </div>
        </div>
      </div>

      {/* Sub-state tabs */}
      <StatusTabs containerTestId="outreach-tabs" value={tab}
        onChange={(s) => { setTab(s); setSelected(null); }}
        tabs={STATES.map((s) => ({
          id: s, label: tf(STATE_FR[s]), count: counts[s] ?? 0,
          tone: { A_RELANCER: "warning", REPONSE_RECUE: "success" }[s] || "neutral",
          testid: `outreach-tab-${s}`, countTestid: `outreach-count-${s}`,
        }))} />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[170px] max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tf("Rechercher un athlète…")}
            className="h-9 pl-8 text-sm" data-testid="outreach-search" />
        </div>
        <Select value={fChannel} onValueChange={setFChannel}>
          <SelectTrigger className="h-9 w-[130px] text-xs" data-testid="outreach-filter-channel"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{tf("Canal : tous")}</SelectItem>
            <SelectItem value="whatsapp">WhatsApp</SelectItem>
            <SelectItem value="email">Email</SelectItem>
            <SelectItem value="phone">{tf("Téléphone")}</SelectItem>
          </SelectContent>
        </Select>
        {sports.length > 1 && (
          <Select value={fSport} onValueChange={setFSport}>
            <SelectTrigger className="h-9 w-[120px] text-xs" data-testid="outreach-filter-sport"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">{tf("Tous les sports")}</SelectItem>
              {sports.map((s) => <SelectItem key={s} value={s}>{tfr(s)}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <Button variant="ghost" size="sm" className="h-9 text-xs" onClick={() => { setQ(""); setFChannel("all"); setFSport("all"); }} data-testid="outreach-reset-filters">
          {tf("Réinitialiser")}
        </Button>
        <Button variant="outline" size="sm" className="h-9 text-xs" onClick={exportCsv} data-testid="outreach-export">
          <Download className="h-3.5 w-3.5 mr-1.5" />{tf("Exporter")}
        </Button>
      </div>

      {/* List + Detail */}
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] gap-4 xl:gap-5 items-start min-w-0">
        <div className="rounded-2xl border border-border bg-card min-w-0 overflow-hidden" data-testid="outreach-list">
          <div className="hidden sm:flex items-center gap-3 px-3 py-2 border-b border-border/70 text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
            <span className="flex-1 min-w-0">{tf("Athlète")}</span>
            <span className="w-10 shrink-0 text-center">{tf("Canal")}</span>
            <span className="hidden md:block w-20 shrink-0">{tf("1er contact")}</span>
            <span className="w-12 shrink-0 text-center">{tf("Relances")}</span>
            <span className="hidden lg:block w-28 shrink-0 text-right">{tf("Prochaine relance")}</span>
          </div>
          {!data ? (
            <div className="p-4 space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-14 rounded-xl bg-secondary/60 animate-pulse" />)}</div>
          ) : list.length === 0 ? (
            <div className="py-14 text-center text-sm text-muted-foreground" data-testid="outreach-empty">
              {tf("Aucun athlète dans cet état.")}
            </div>
          ) : (
            <ul className="divide-y divide-border/70">
              {list.map((a) => {
                const CIcon = CHANNEL_ICON[a.first_contact_channel] || MessageCircle;
                const fl = followUpLabel(a.next_follow_up_days, tf);
                const showFl = !["REPONSE_RECUE", "REFUS", "SANS_REPONSE"].includes(a.contact_state);
                return (
                  <li key={a.id}>
                    <div role="button" tabIndex={0}
                      onClick={() => { setSelected(a.id); if (window.matchMedia("(max-width: 1279px)").matches) setMobileOpen(true); }}
                      onKeyDown={(e) => { if (e.key === "Enter") { setSelected(a.id); if (window.matchMedia("(max-width: 1279px)").matches) setMobileOpen(true); } }}
                      className={cn("w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors cursor-pointer min-w-0",
                        current?.id === a.id ? "bg-secondary/60 border-l-2 border-l-foreground/70" : "hover:bg-secondary/50 border-l-2 border-l-transparent")}
                      data-testid={`outreach-row-${a.id}`}>
                      <Avatar className="h-9 w-9 shrink-0">
                        <AvatarImage src={a.photo_url} alt="" />
                        <AvatarFallback className="text-[11px]">{initialsOf(a.name)}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm font-semibold truncate">{a.name}</span>
                          {a.minor && <span className="shrink-0 text-[10px] font-semibold uppercase rounded-full border border-warning/40 bg-warning/10 text-warning px-1.5 py-px" data-testid={`minor-badge-${a.id}`}>{tf("Mineur")}</span>}
                        </div>
                        <div className="text-[11px] text-muted-foreground truncate">
                          {[a.club || tf("Sans club"), a.position, a.age ? `${a.age} ans` : null].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                      <div className="w-10 shrink-0 flex justify-center" aria-label={CHANNEL_FR[a.first_contact_channel] || ""} title={CHANNEL_FR[a.first_contact_channel] || ""}>
                        <CIcon className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="hidden md:block w-20 shrink-0 text-xs tabular-nums">{fmtDate(a.first_contact_at)}</div>
                      <div className="w-12 shrink-0 text-center text-xs tabular-nums" data-testid={`attempts-${a.id}`}>{a.contact_attempts}/{a.max_attempts}</div>
                      <div className="hidden lg:block w-28 shrink-0 text-right">
                        {showFl && a.next_follow_up_date ? (
                          <>
                            <div className="text-xs tabular-nums">{fmtDate(a.next_follow_up_date)}</div>
                            <div className={cn("text-[11px] font-medium", fl?.tone === "overdue" ? "text-athlynx" : fl?.tone === "today" ? "text-warning" : "text-muted-foreground")}>{fl?.text}</div>
                          </>
                        ) : <span className="text-xs text-muted-foreground/60">—</span>}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="hidden xl:block min-w-0">
          {current ? <OutreachDetail key={current.id} a={current} tf={tf} onChanged={load} /> : (
            <div className="rounded-2xl border border-dashed border-border py-16 text-center text-sm text-muted-foreground">
              {tf("Sélectionnez un athlète")}
            </div>
          )}
        </div>
      </div>

      <div className="xl:hidden">
        <Sheet open={mobileOpen && !!current} onOpenChange={setMobileOpen}>
          <SheetContent side="bottom" className="inset-x-2 min-h-[60dvh] max-h-[calc(100dvh-104px)] overflow-y-auto rounded-t-2xl border p-4 pb-[max(16px,env(safe-area-inset-bottom))]">
            <SheetTitle className="sr-only">{current?.name || tf("Athlète")}</SheetTitle>
            <SheetDescription className="sr-only">{tf("Détail de l'athlète sélectionné")}</SheetDescription>
            {current && <OutreachDetail key={current.id} a={current} tf={tf} onChanged={load} />}
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
}
